import { useState, useCallback, useRef, useEffect } from "react";

export function useSoundEffects() {
  const [enabled, setEnabled] = useState(true);
  const audioContextRef = useRef(null);

  useEffect(() => {
    audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
    return () => {
      audioContextRef.current?.close();
    };
  }, []);

  const playTone = useCallback((frequency, duration, type = 'sine', volume = 0.12) => {
    if (!enabled || !audioContextRef.current) return;

    try {
      const ctx = audioContextRef.current;
      const oscillator = ctx.createOscillator();
      const gainNode = ctx.createGain();

      oscillator.type = type;
      oscillator.frequency.setValueAtTime(frequency, ctx.currentTime);
      oscillator.frequency.exponentialRampToValueAtTime(frequency * 0.7, ctx.currentTime + duration);

      gainNode.gain.setValueAtTime(0, ctx.currentTime);
      gainNode.gain.linearRampToValueAtTime(volume, ctx.currentTime + 0.02);
      gainNode.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + duration);

      oscillator.connect(gainNode);
      gainNode.connect(ctx.destination);

      oscillator.start(ctx.currentTime);
      oscillator.stop(ctx.currentTime + duration);
    } catch (e) {
      // Silent fail
    }
  }, [enabled]);

  const playOpen = useCallback(() => {
    playTone(720, 0.18, 'sine', 0.08);
    setTimeout(() => playTone(900, 0.18, 'sine', 0.06), 60);
  }, [playTone]);

  const playClose = useCallback(() => {
    playTone(540, 0.18, 'sine', 0.08);
    setTimeout(() => playTone(440, 0.18, 'sine', 0.06), 60);
  }, [playTone]);

  const playSend = useCallback(() => {
    playTone(880, 0.06, 'sine', 0.05);
  }, [playTone]);

  const playReceive = useCallback(() => {
    playTone(660, 0.12, 'sine', 0.06);
    setTimeout(() => playTone(520, 0.15, 'sine', 0.05), 70);
  }, [playTone]);

  const playAttach = useCallback(() => {
    playTone(1000, 0.05, 'sine', 0.04);
  }, [playTone]);

  const playVoiceStart = useCallback(() => {
    playTone(520, 0.1, 'sine', 0.06);
    setTimeout(() => playTone(660, 0.1, 'sine', 0.05), 60);
  }, [playTone]);

  const playVoiceStop = useCallback(() => {
    playTone(660, 0.1, 'sine', 0.05);
    setTimeout(() => playTone(520, 0.12, 'sine', 0.04), 60);
  }, [playTone]);

  const playError = useCallback(() => {
    playTone(330, 0.15, 'sine', 0.06);
    setTimeout(() => playTone(280, 0.15, 'sine', 0.05), 100);
  }, [playTone]);

  const playNotification = useCallback(() => {
    playTone(760, 0.18, 'sine', 0.07);
    setTimeout(() => playTone(960, 0.18, 'sine', 0.06), 120);
  }, [playTone]);

  return {
    playOpen,
    playClose,
    playSend,
    playReceive,
    playAttach,
    playVoiceStart,
    playVoiceStop,
    playError,
    playNotification,
    setEnabled,
    enabled,
  };
}

export default useSoundEffects;